import { CONST } from '../utils/constants';
import DateUtils from '../utils/dateUtils';
import { i18nGlobal } from '@/i18n';

export type InvoicePeriodType = 0 | 1 | 2 | 3 | 4;
export const InvoicePeriodType = {
  Month: 0 as InvoicePeriodType,
  Quarter: 1 as InvoicePeriodType,
  Single: 2 as InvoicePeriodType,
  Year: 3 as InvoicePeriodType,
  HalfYear: 4 as InvoicePeriodType,
};

export type InvoicePaymentStatus = 0 | 1 | 2 | 3;
export const InvoicePaymentStatus = {
  Open: 0 as InvoicePaymentStatus,
  Paid: 1 as InvoicePaymentStatus,
  Cancelled: 2 as InvoicePaymentStatus,
  PartiallyPaid: 3 as InvoicePaymentStatus,
};

export type DunningLevel = 0 | 1 | 2 | 3 | 4 | 5;

export const MidMonthThresholdDay = 15;

export interface InvoiceStatusSelectItem {
  value: InvoicePaymentStatus;
  icon: string;
  color: string;
  tooltip: string;
}

export interface Invoice {
  id: string; // System.Guid
  invoiceNumber: number;
  attendeeAppUserId: string; // System.Guid,
  attendeeSalutation: string;
  attendeeTitle: string;
  attendeeLastName: string;
  attendeeFirstName: string;
  attendeeFullName: string;
  attendeeZip: string;
  attendeeCity: string;
  attendeeStreet: string;
  periodStartDate: string; // System.DateTime
  periodEndDate: string; // System.DateTime
  periodType: InvoicePeriodType;
  periodicCostInvoice: boolean;
  paymentStatus: InvoicePaymentStatus;
  invoiceRunId?: string; // System.Guid
  dueDate?: string; // System.DateTime
  dueDateSetDateTime?: string; // System.DateTime
  dueDateSetByEmployeeId?: string; // System.Guid
  dunningLevel: DunningLevel;
  dunningLevel1Date?: string;
  dunningLevel2Date?: string;
  dunningLevel3Date?: string;
  dunningLevel4Date?: string;
  dunningLevel5Date?: string;
  exported: boolean;
  exportedDateTime?: string; // System.DateTime
  exportedByEmployeeId?: string; // System.Guid
  markedPaidDateTime?: string; // System.DateTime
  markedPaidByEmployeeId?: string; // System.Guid
  markedCancelledDateTime?: string; // System.DateTime
  markedCancelledByEmployeeId?: string; // System.Guid
  markedPartiallyPaidDateTime?: string; // System.DateTime
  markedPartiallyPaidByEmployeeId?: string; // System.Guid
  partiallyPaidCentAmount?: number;
  companyId: string; // System.Guid
  createdDate: string; // System.DateTime
  complete: boolean;
  centCostSum: number;
  memberNumber: string;
  readonly euroCostSum: number;
  readonly partiallyPaidEuroAmount: number;
  readonly typeString: string;
  readonly periodString: string;
}

export interface Value {
  value: Invoice[];
  '@odata.context'?: string | undefined;
  '@odata.count'?: number | undefined;
}

function toAPI(data: Partial<Invoice>): any {
  return {
    Id: data?.id ?? CONST.emptyGuid,
    InvoiceNumber: data?.invoiceNumber ?? 0,
    AttendeeAppUserId: data?.attendeeAppUserId ?? CONST.emptyGuid,
    AttendeeSalutation: data?.attendeeSalutation ?? '',
    AttendeeTitle: data?.attendeeTitle ?? '',
    AttendeeLastName: data?.attendeeLastName ?? '',
    AttendeeFirstName: data?.attendeeFirstName ?? '',
    AttendeeZip: data?.attendeeZip ?? '',
    AttendeeStreet: data?.attendeeStreet ?? '',
    AttendeeCity: data?.attendeeCity ?? '',
    PeriodStartDate:
      data?.periodStartDate ?? new Date().toISOString().substring(0, 10),
    PeriodEndDate:
      data?.periodEndDate ?? new Date().toISOString().substring(0, 10),
    PeriodType: data?.periodType ?? InvoicePeriodType.Single,
    PaymentStatus: data?.paymentStatus ?? InvoicePaymentStatus.Open,
    PeriodicCostInvoice: data?.periodicCostInvoice ?? false,
    InvoiceRunId: data?.invoiceRunId ?? undefined,
    DueDate: DateUtils.dateNullableToApi(data?.dueDate),
    DunningLevel: data?.dunningLevel ?? 0,
    DunningLevel1Date: data?.dunningLevel1Date || undefined,
    DunningLevel2Date: data?.dunningLevel2Date || undefined,
    DunningLevel3Date: data?.dunningLevel3Date || undefined,
    DunningLevel4Date: data?.dunningLevel4Date || undefined,
    DunningLevel5Date: data?.dunningLevel5Date || undefined,
    Exported: data?.exported ?? false,
    ExportedDateTime: DateUtils.dateNullableToApi(data?.exportedDateTime),
    ExportedByEmployeeId: data?.exportedByEmployeeId ?? undefined,
    MarkedPaidDateTime: DateUtils.dateNullableToApi(data?.markedPaidDateTime),
    MarkedPaidByEmployeeId: data?.markedPaidByEmployeeId ?? undefined,
    MarkedCancelledDateTime: DateUtils.dateNullableToApi(
      data?.markedCancelledDateTime
    ),
    MarkedCancelledByEmployeeId: data?.markedCancelledByEmployeeId ?? undefined,
    MarkedPartiallyPaidDateTime: DateUtils.dateNullableToApi(
      data?.markedPartiallyPaidDateTime
    ),
    MarkedPartiallyPaidByEmployeeId:
      data?.markedPartiallyPaidByEmployeeId ?? undefined,
    PartiallyPaidCentAmount: data?.partiallyPaidCentAmount ?? undefined,
    CompanyId: data?.companyId ?? CONST.emptyGuid,
    CreatedDate: data?.createdDate ?? new Date().toISOString().substring(0, 10),
    DueDateSetDateTime: data?.dueDateSetDateTime ?? undefined,
    DueDateSetByEmployeeId: data?.dueDateSetByEmployeeId ?? undefined,
  };
}

function parse(data: Partial<Invoice>): Invoice {
  let periodType =
    data?.periodType ?? getPeriodTypeText(InvoicePeriodType.Single);
  let paymentStatus =
    data?.paymentStatus ?? getPaymentStatusText(InvoicePaymentStatus.Open);

  return {
    id: data?.id ?? CONST.emptyGuid,
    invoiceNumber: data?.invoiceNumber ?? 0,
    attendeeAppUserId: data?.attendeeAppUserId ?? CONST.emptyGuid,
    attendeeSalutation: data?.attendeeSalutation ?? '',
    attendeeTitle: data?.attendeeTitle ?? '',
    attendeeLastName: data?.attendeeLastName ?? '',
    attendeeFirstName: data?.attendeeFirstName ?? '',
    attendeeFullName: data?.attendeeFullName ?? '',
    attendeeZip: data?.attendeeZip ?? '',
    attendeeCity: data?.attendeeCity ?? '',
    attendeeStreet: data?.attendeeStreet ?? '',
    periodStartDate:
      data?.periodStartDate ?? new Date().toISOString().substring(0, 10),
    periodEndDate:
      data?.periodEndDate ?? new Date().toISOString().substring(0, 10),
    periodType: InvoicePeriodType[periodType as keyof typeof InvoicePeriodType],
    paymentStatus:
      InvoicePaymentStatus[paymentStatus as keyof typeof InvoicePaymentStatus],
    periodicCostInvoice: data?.periodicCostInvoice ?? false,
    invoiceRunId: data?.invoiceRunId ?? undefined,
    dueDate: data?.dueDate ?? undefined,
    dueDateSetDateTime: data?.dueDateSetDateTime ?? undefined,
    dueDateSetByEmployeeId: data?.dueDateSetByEmployeeId ?? undefined,
    dunningLevel: data?.dunningLevel ?? 0,
    dunningLevel1Date: data?.dunningLevel1Date || undefined,
    dunningLevel2Date: data?.dunningLevel2Date || undefined,
    dunningLevel3Date: data?.dunningLevel3Date || undefined,
    dunningLevel4Date: data?.dunningLevel4Date || undefined,
    dunningLevel5Date: data?.dunningLevel5Date || undefined,
    exported: data?.exported ?? false,
    exportedDateTime: data?.exportedDateTime ?? undefined,
    exportedByEmployeeId: data?.exportedByEmployeeId ?? undefined,
    markedPaidDateTime: data?.markedPaidDateTime ?? undefined,
    markedPaidByEmployeeId: data?.markedPaidByEmployeeId ?? undefined,
    markedCancelledDateTime: data?.markedCancelledDateTime ?? undefined,
    markedCancelledByEmployeeId: data?.markedCancelledByEmployeeId ?? undefined,
    markedPartiallyPaidDateTime: data?.markedPartiallyPaidDateTime ?? undefined,
    markedPartiallyPaidByEmployeeId:
      data?.markedPartiallyPaidByEmployeeId ?? undefined,
    partiallyPaidCentAmount: data?.partiallyPaidCentAmount ?? undefined,
    companyId: data?.companyId ?? CONST.emptyGuid,
    createdDate: data?.createdDate ?? new Date().toISOString().substring(0, 10),
    complete: data?.complete ?? false,
    centCostSum: data?.centCostSum ?? 0,
    memberNumber: data?.memberNumber ?? '',
    get euroCostSum(): number {
      return this.centCostSum * 0.01;
    },
    get partiallyPaidEuroAmount(): number {
      return this.partiallyPaidCentAmount
        ? this.partiallyPaidCentAmount * 0.01
        : 0;
    },
    get typeString(): string {
      switch (this.periodType) {
        case InvoicePeriodType.Quarter:
          if (this.periodicCostInvoice)
            return i18nGlobal.t('app.invoice.membership-invoice-quarterly');
          return i18nGlobal.t('app.invoice.collective-invoice-quarterly');
        case InvoicePeriodType.Month:
          if (this.periodicCostInvoice)
            return i18nGlobal.t('app.invoice.membership-invoice-monthly');
          return i18nGlobal.t('app.invoice.collective-invoice-monthly');
        case InvoicePeriodType.Year:
          if (this.periodicCostInvoice)
            return i18nGlobal.t('app.invoice.membership-invoice-yearly');
          return i18nGlobal.t('app.invoice.collective-invoice-yearly');
        case InvoicePeriodType.HalfYear:
          if (this.periodicCostInvoice)
            return i18nGlobal.t('app.invoice.membership-invoice-half-yearly');
          return i18nGlobal.t('app.invoice.collective-invoice-half-yearly');
        default:
          return i18nGlobal.t('app.invoice.single-invoice');
      }
    },
    get periodString(): string {
      if (this.periodType == InvoicePeriodType.Single) {
        return `${DateUtils.formatDateWithLocale(this.periodStartDate)}`;
      }

      return `${DateUtils.formatDateWithLocale(
        this.periodStartDate
      )} - ${DateUtils.formatDateWithLocale(this.periodEndDate)}`;
    },
  };
}

function getPeriodTypeText(type: InvoicePeriodType) {
  return Object.keys(InvoicePeriodType)[
    Object.values(InvoicePeriodType).findIndex((entry) => entry == type)
  ];
}

export function getPaymentStatusText(type: InvoicePaymentStatus) {
  return Object.keys(InvoicePaymentStatus)[
    Object.values(InvoicePaymentStatus).findIndex((entry) => entry == type)
  ];
}

export default {
  parse,
  toAPI: toAPI,
};
